// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Clones the track once for a gapless loop and animates with the Web Animations API.
// Pauses on hover, keyboard focus, hidden tab, or an explicit (sticky) pause command.
// Under prefers-reduced-motion it never animates and exposes data-static so the row can scroll manually.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #resizeObserver = null;
  #resizeFrame = 0;
  #lastWidth = 0;
  #pause = { hover: false, focus: false, hidden: false, user: false };

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal });

    this.addEventListener("pointerenter", () => this.#setPause("hover", true), { signal });
    this.addEventListener("pointerleave", () => this.#setPause("hover", false), { signal });
    this.addEventListener("focusin", () => this.#setPause("focus", true), { signal });
    this.addEventListener("focusout", (event) => {
      if (!this.contains(event.relatedTarget)) this.#setPause("focus", false);
    }, { signal });
    document.addEventListener("visibilitychange", () => this.#setPause("hidden", document.hidden), { signal });

    // Invoker command from the layout-owned pause/play button.
    this.addEventListener("command", (event) => {
      if (event.command !== "--toggle") return;
      this.#setPause("user", !this.#pause.user);
      this.#syncToggle(event.source);
    }, { signal });

    const track = this.querySelector("[data-marquee-track]");
    if (track) {
      this.#resizeObserver = new ResizeObserver(() => {
        cancelAnimationFrame(this.#resizeFrame);
        this.#resizeFrame = requestAnimationFrame(() => {
          const width = this.clientWidth;
          if (width !== this.#lastWidth) {
            this.#lastWidth = width;
            this.#setup();
          }
        });
      });
      this.#resizeObserver.observe(this);
    }

    document.fonts?.ready.then(() => {
      if (!signal.aborted) this.#setup();
    });

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#resizeFrame);
    this.#animation?.cancel();
  }

  #setPause(source, value) {
    this.#pause[source] = value;
    this.#applyPlayState();
  }

  #applyPlayState() {
    if (!this.#animation) return;
    const paused = Object.values(this.#pause).some(Boolean);
    if (paused) this.#animation.pause();
    else this.#animation.play();
  }

  #syncToggle(button) {
    if (!button) return;
    const paused = this.#pause.user;
    button.setAttribute("aria-pressed", paused ? "true" : "false");
    const label = paused ? button.dataset.playLabel : button.dataset.pauseLabel;
    if (label) button.setAttribute("aria-label", label);
    button.querySelector("[data-icon-pause]")?.toggleAttribute("hidden", paused);
    button.querySelector("[data-icon-play]")?.toggleAttribute("hidden", !paused);
  }

  #setup() {
    // Remember how far through the loop we are so a re-measure continues instead of snapping back to the start.
    let progress = 0;
    const previous = this.#animation;
    if (previous) {
      const duration = Number(previous.effect?.getTiming().duration) || 0;
      const time = Number(previous.currentTime) || 0;
      if (duration > 0) progress = (time % duration) / duration;
    }
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    if (!track) return;

    if (this.#mql.matches) {
      this.toggleAttribute("data-static", true);
      return;
    }
    this.toggleAttribute("data-static", false);

    // Rebuild exactly two copies on every setup so the loop stays seamless even after the editor or a
    // re-render swaps the content; the copy stays out of the a11y tree and tab order.
    track.querySelectorAll(":scope > [data-marquee-clone]").forEach((node) => node.remove());
    const clones = [...track.children].map((node) => {
      const clone = node.cloneNode(true);
      clone.setAttribute("aria-hidden", "true");
      clone.setAttribute("data-marquee-clone", "");
      clone.inert = true;
      return clone;
    });
    track.append(...clones);

    // One loop = half the track plus one gap, so the seam between the copies matches every other gap.
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = (track.scrollWidth + gap) / 2;
    if (distance <= 0) return;

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const rtl = getComputedStyle(this).direction === "rtl";
    const reverse = (this.dataset.direction === "right") !== rtl;
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;
    const sign = rtl ? -1 : 1;

    this.#animation = track.animate(
      [{ transform: `translateX(${from * sign}px)` }, { transform: `translateX(${to * sign}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
    this.#animation.currentTime = progress * durationMs;
    this.#applyPlayState();
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
